import { mapValues, omit } from 'es-toolkit';

import { FileManagerResult } from '@paragon/component-processor';
import { Job, Region, ShallowPdf } from '@paragon/contract-library';

import { getFullName } from '../fileProcessingUtilities';
import { SealingService } from './connections';

export interface NormalizedJob extends Job {
  organizationOwnedBy: string;
}

function normalizeJob(job: Job): NormalizedJob {
  return {
    ...job,
    organizationOwnedBy: job.ownerOrganization,
  };
}

export function pOpen(markupUrl: string, jobId: number) {
  const pdfInMarkupClientUrl = `${markupUrl}?jobId=${jobId}`;
  window.open(pdfInMarkupClientUrl, '_blank');
}

export async function pFetchAll() {
  const jobs = await SealingService.get<readonly Job[]>('Jobs/');

  return jobs.map(normalizeJob);
}

export function pFetch(id: number): Promise<Job> {
  return SealingService.get(`jobs/${id}`);
}

export function pFetchShallowPdfs(id: number): Promise<readonly ShallowPdf[]> {
  return SealingService.get(`jobs/${id}/pdfs/shallow`);
}

interface Creator {
  readonly email: string;
  readonly organization: string;
}

interface CreateJobFromZipParameters {
  readonly creator: Creator;
  readonly fileManagerData: FileManagerResult;
}

export function pCreateJobFromZip(
  parameters: CreateJobFromZipParameters & Partial<Job>,
  base64Zip: string,
  convertToParagonTdds: boolean,
) {
  const serverJobInsertBody: Partial<Job> & { fileManagerData?: FileManagerResult } = {
    ...parameters,
    creatorEmail: parameters.creator.email,
    ownerOrganization: parameters.creator.organization,
  };

  delete serverJobInsertBody.fileManagerData;

  const componentDesigns = mapValues(parameters.fileManagerData.componentDesigns, (components) =>
    components.map(getFullName),
  );
  const miscellaneousFiles = Object.values(parameters.fileManagerData.miscellaneousFiles).map(getFullName);
  const metaPdfs = mapValues(parameters.fileManagerData.metaPdfs, (metaPdf) => getFullName(metaPdf));
  const fileManagerData = {
    componentDesigns,
    miscellaneousFiles: miscellaneousFiles || [],
    metaPdfs,
  };

  return SealingService.post<Job>('jobs/createJobFromZip', {
    data: {
      job: serverJobInsertBody,
      fileManagerData,
      base64Zip,
      convertToParagonTdds,
    },
  }).then(normalizeJob);
}

export function pEditJob(job: Partial<NormalizedJob>): Promise<void> {
  const serverJobObject = omit(job, ['componentDesigns']);
  return SealingService.patch(`jobs/editjob/${job.id}`, { data: serverJobObject });
}

export function pShareWithOrganizations(jobId: number, organizationNames: readonly string[]): Promise<void> {
  return SealingService.post(`jobs/${jobId}/share`, { data: organizationNames });
}

export function pAssignToUser(jobId: number, userEmails: readonly string[]): Promise<void> {
  return SealingService.patch(`jobs/${jobId}/assigntouser`, {
    params: new URLSearchParams({ userEmails: userEmails.join(',') }),
  });
}

export function pSetStatus(jobId: number, status: string): Promise<void> {
  return SealingService.patch(`jobs/${jobId}/setstatus`, { data: status });
}

export function downloadMultiMergePdf(
  pdfIds: readonly number[],
  generateMultiMergeCoverPdf: boolean,
  multiMergeJobName: string,
) {
  const data = {
    pdfIds,
    generateMultiMergeCoverPdf,
    multiMergeJobName,
  };

  return SealingService.fetchFile('jobs/generateMultiMergePdf', {
    post: true,
    data,
    mightEmail: true,
    asUrl: true,
  });
}

export function pFetchAllRegions(): Promise<Region[]> {
  return SealingService.get('regions');
}
